import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterLink } from '@angular/router';
import { UserApplications } from '@workspace/data-types';
import { ApplicationStore, UserStore } from '@workspace/ng-data-access';
import { filter, map, startWith } from 'rxjs';

@Component({
  selector: 'etxe-agent-subscription-banner',
  imports: [RouterLink, MatButtonModule, MatIconModule],
  templateUrl: './agent-subscription-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentSubscriptionBannerComponent {
  private readonly router = inject(Router);
  private readonly userStore = inject(UserStore);
  private readonly appStore = inject(ApplicationStore);

  /**
   * Optional override to manually force banner visibility (useful for testing or previews).
   */
  readonly forceVisible = input<boolean | undefined>(undefined);

  readonly isAgent = computed(() => !!this.userStore.current()?.profiles?.agent);
  readonly isAgentApp = computed(() => this.appStore.currentApplicationName() === UserApplications.AGENT);
  readonly isSubscribed = computed(() => this.userStore.isSubscribed());
  readonly hasBeenSubscribed = computed(() => this.userStore.hasBeenSubscribed());

  /**
   * Reactively checks if any activated route segment in the current navigation hierarchy
   * defines `data: { showSubscriptionBanner: true }`.
   */
  readonly hasRouteBannerData = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.checkRouteBannerData()),
      startWith(this.checkRouteBannerData()),
    ),
    { initialValue: this.checkRouteBannerData() },
  );

  readonly isVisible = computed(() => {
    if (this.forceVisible() !== undefined) {
      return !!this.forceVisible();
    }
    return this.isAgentApp() && this.isAgent() && !this.isSubscribed() && this.hasRouteBannerData();
  });

  private checkRouteBannerData(): boolean {
    let current: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (current) {
      if (current.data?.['showSubscriptionBanner'] === true) {
        return true;
      }
      current = current.firstChild;
    }
    return false;
  }
}
